import { Button, Grid, MenuItem, TextField } from "@material-ui/core";
import { Alert, Skeleton } from "@material-ui/lab";
import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { MdDeleteOutline, MdEdit, MdHistory } from "react-icons/md";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { CREATE_NEW_ITEM } from "../../../../constants/constants";
import { useQuery } from "../../../../hooks/useQuery";
import { planEatApi } from "../../../../planEatApi/PlanEatApi";
import { IEaterGroupContent } from "../../../../planEatApi/PlanEatFacade";
import { AdminPicker } from "../../components/AdminPicker";
import { EntityDetailLayout, Section } from "../../components/EntityDetailLayout";
import { EntityEditorConfig, useEntityEditor } from "../../components/useEntityEditor";
import { hierarchyCrumbs } from "../../components/VersionsPage";
import { useSysadminData } from "../../SysadminData";
import { errorMessage, safeDecode, sysadminPaths } from "../../sysadminUtils";
import { buildAccounts } from "../../accounts";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { EntityDialog } from "../../components/EntityDialog";
import { PageHeader } from "../../components/PageHeader";
import { SetupIssues } from "../../components/SetupIssues";
import { eaterGroupIssues, IssueAction } from "../../setupCheck";
import { AddMembersDialog } from "../User/AddMembersDialog";
import { InviteUserDialog } from "../User/InviteUserDialog";
import { UsersTable } from "../User/Users";
import { officeOptions } from "../../components/selectOptions";

const eaterGroupConfig: EntityEditorConfig<IEaterGroupContent> = {
    label: "Eater group",
    section: "eaterGroups",
    empty: {
        id: "",
        name: "",
        description: "",
        officeId: "",
        eaterGroupName: "",
        eaterGroupCVR: "",
        eaterGroupAddress: "",
        eaterGroupPhoneNumber: "",
        eaterGroupAdminIds: [],
    },
    get: planEatApi.getEaterGroup,
    getVersion: planEatApi.getVersionForEaterGroup,
    post: planEatApi.postEaterGroup,
    remove: planEatApi.postDeleteForEaterGroup,
};

/** The eater group with its members and what is missing. Creating and historic versions use the form below. */
export const EaterGroup: React.FC = () => {
    const { eaterGroupId, version } = useParams<{ eaterGroupId?: string; version?: string }>();
    if (eaterGroupId && eaterGroupId !== CREATE_NEW_ITEM && !version) return <EaterGroupView id={safeDecode(eaterGroupId)} />;
    return <EaterGroupForm />;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const EaterGroupView: React.FC<{ id: string }> = ({ id }) => {
    const data = useSysadminData();
    const history = useHistory();
    const location = useLocation();
    const [editing, setEditing] = useState(false);
    const [adding, setAdding] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);

    const group = data.eaterGroupById(id);
    const accounts = useMemo(() => buildAccounts(data), [data]);
    const issues = useMemo(() => eaterGroupIssues(data, accounts, id), [data, accounts, id]);

    if (!group) {
        return (
            <div>
                <PageHeader title="Eater group" breadcrumbs={[{ label: "Eater groups", to: sysadminPaths.list("eaterGroups") }]} />
                {data.loading ? <Skeleton height={120} /> : (
                    <Alert severity="error" action={<Button color="inherit" size="small" onClick={data.reload}>Retry</Button>}>
                        {data.error ?? "The eater group was not found. It may have been deleted."}
                    </Alert>
                )}
            </div>
        );
    }

    const office = data.officeById(group.officeId);
    const company = data.companyById(office?.companyId);
    const members = accounts.filter(a => a.user?.eaterGroupId === id).length;
    const admins = group.eaterGroupAdminIds ?? [];
    const name = group.eaterGroupName || "Unnamed eater group";
    const details = [group.eaterGroupCVR && `CVR ${group.eaterGroupCVR}`, group.eaterGroupAddress, group.eaterGroupPhoneNumber].filter(Boolean).join(" · ");

    function openUser(email: string) {
        const params = new URLSearchParams(location.search);
        params.set("user", email);
        history.push({ pathname: location.pathname, search: params.toString() });
    }

    function runAction(action: IssueAction) {
        if (action.kind === "addMembers") setAdding(true);
        else setEditing(true);
    }

    async function remove() {
        try {
            await planEatApi.postDeleteForEaterGroup(id);
            toast.success("Eater group deleted");
            await data.reload();
            history.push(sysadminPaths.list("eaterGroups"));
        } catch (e) {
            toast.error(errorMessage(e, "Could not delete eater group"));
            setConfirmDelete(false);
        }
    }

    return (
        <div>
            <PageHeader
                title={name}
                breadcrumbs={[...hierarchyCrumbs(data, "eaterGroups", { officeId: group.officeId }), { label: name }]}
                subtitle={<>
                    <div>{[office?.officeName, company?.companyName].filter(Boolean).join(" › ") || "Unknown office"} · {plural(members, "member", "members")}</div>
                    <div>{admins.length ? `Admin: ${admins.join(", ")}` : "No admin"}{details && ` · ${details}`}</div>
                </>}
                actions={<>
                    <Button variant="outlined" startIcon={<MdEdit />} onClick={() => setEditing(true)}>Edit</Button>
                    <Button variant="outlined" startIcon={<MdHistory />} onClick={() => history.push(sysadminPaths.versions("eaterGroups", id))}>History</Button>
                    <Button style={{ color: "red" }} startIcon={<MdDeleteOutline />} onClick={() => setConfirmDelete(true)}>Delete</Button>
                </>}
            />
            <SetupIssues issues={issues} okText="Everything is set up – all members can book." onAction={runAction} onOpenUser={openUser} />
            <UsersTable eaterGroupId={id} officeId={group.officeId} title="Members" />

            {editing && <EntityDialog target={{ kind: "eaterGroup", id }} pageLink={false} onClose={() => setEditing(false)} />}
            {adding && (
                <AddMembersDialog eaterGroupId={id} onClose={() => setAdding(false)} onInvite={() => { setAdding(false); setInviting(true); }} />
            )}
            {inviting && (
                <InviteUserDialog
                    prefill={{ officeId: group.officeId, eaterGroupId: id }}
                    onClose={() => setInviting(false)}
                    onInvited={email => { setInviting(false); openUser(email); }}
                    onOpenExisting={email => { setInviting(false); openUser(email); }}
                />
            )}
            <ConfirmDialog
                open={confirmDelete}
                title="Delete eater group?"
                message={<>
                    <p><b>{name}</b> is deleted. It can be restored from the history.</p>
                    {members > 0 && <p>The eater group still has <b>{plural(members, "member", "members")}</b>. They are not deleted, but cannot book until they are placed in another eater group.</p>}
                </>}
                confirmLabel="Delete"
                destructive
                onConfirm={remove}
                onClose={() => setConfirmDelete(false)}
            />
        </div>
    );
};

const EaterGroupForm: React.FC = () => {
    const { eaterGroupId, version } = useParams<{ eaterGroupId?: string; version?: string }>();
    const query = useQuery();
    const data = useSysadminData();
    const editor = useEntityEditor(eaterGroupConfig, eaterGroupId, version, { officeId: query.get("officeId") ?? "" });
    const { entity: eaterGroup, setField, isVersion } = editor;

    const missing: string[] = [];
    if (!eaterGroup.eaterGroupName.trim()) missing.push("name");
    if (!eaterGroup.officeId) missing.push("office");
    if ((eaterGroup.eaterGroupAdminIds ?? []).length === 0) missing.push("at least one admin");

    const userCount = data.users.filter(u => u.content.eaterGroupId === eaterGroup.id).length;
    const title = editor.isNew ? "New eater group" : eaterGroup.eaterGroupName || "Unnamed eater group";
    const officeKnown = !!data.officeById(eaterGroup.officeId);

    return (
        <EntityDetailLayout
            label="Eater group"
            section="eaterGroups"
            title={title}
            breadcrumbs={[...hierarchyCrumbs(data, "eaterGroups", { officeId: eaterGroup.officeId }), { label: title }]}
            editor={editor}
            missing={missing}
            parentPath={eaterGroup.officeId && officeKnown ? sysadminPaths.detail("offices", eaterGroup.officeId) : sysadminPaths.list("eaterGroups")}
            deleteWarning={userCount > 0 ? <p>The eater group still has <b>{userCount} user(s)</b>. They are not deleted automatically.</p> : undefined}
            related={<UsersTable eaterGroupId={eaterGroup.id} officeId={eaterGroup.officeId} title="Users" />}
        >
            <Section title="Details">
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth required variant="outlined" label="Name" value={eaterGroup.eaterGroupName} disabled={isVersion}
                            onChange={e => setField("eaterGroupName", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField fullWidth required select variant="outlined" label="Office" value={eaterGroup.officeId} disabled={isVersion}
                            onChange={e => setField("officeId", e.target.value)}
                            helperText={eaterGroup.officeId && !officeKnown && !data.loading ? "The office no longer exists" : undefined}>
                            {officeOptions(data)}
                            {eaterGroup.officeId && !officeKnown && <MenuItem value={eaterGroup.officeId} disabled>Unknown office</MenuItem>}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth variant="outlined" label="CVR" value={eaterGroup.eaterGroupCVR} disabled={isVersion}
                            onChange={e => setField("eaterGroupCVR", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth variant="outlined" label="Phone number" value={eaterGroup.eaterGroupPhoneNumber} disabled={isVersion}
                            onChange={e => setField("eaterGroupPhoneNumber", e.target.value)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField fullWidth variant="outlined" label="Address" value={eaterGroup.eaterGroupAddress} disabled={isVersion}
                            onChange={e => setField("eaterGroupAddress", e.target.value)} />
                    </Grid>
                </Grid>
            </Section>
            <Section title="Admins">
                <AdminPicker value={eaterGroup.eaterGroupAdminIds ?? []} readOnly={isVersion} onChange={ids => setField("eaterGroupAdminIds", ids)} />
            </Section>
        </EntityDetailLayout>
    );
};
